import {useQuery} from "@apollo/client";
import React from "react";
import {FieldErrors, FormProvider, useForm} from "react-hook-form";

import {SliderField} from "@/components/form/fields/SliderField/SliderField.tsx";
import FormErrorAlerts from "@/components/form/FormErrorAlerts.tsx";
import {ServiceField} from "@/lib/constants/service.ts";
import {GQL_GET_MSP_INTERVALS_GROUPS, MspInterval} from "@/lib/queries/checks.ts";
import {ServerErrors} from "@/lib/types/common.ts";
import {EditCheckIntervalsFormValues} from "@/pages/checks/list/actions/bulkSetIntervals/BulkSetIntervalsModal.tsx";

export interface BulkSetIntervalsFormProps {
  formId: string;
  onSubmit: (data: EditCheckIntervalsFormValues) => void;
  onError?: (errors: FieldErrors<EditCheckIntervalsFormValues>, event?: React.BaseSyntheticEvent) => void;
  serverErrors?: ServerErrors;
  note?: string;
  defaultInterval?: number;
  min?: number;
  max?: number;
}

export const BulkSetIntervalsForm = ({
  formId,
  onSubmit,
  onError,
  serverErrors,
  note,
  defaultInterval,
  min,
  max,
}: BulkSetIntervalsFormProps) => {
  const {data, loading, previousData} = useQuery<MspInterval>(GQL_GET_MSP_INTERVALS_GROUPS);

  const methods = useForm<EditCheckIntervalsFormValues>({
    defaultValues: {
      msp_interval: defaultInterval ?? 5,
    },
  });

  const currentData = loading ? previousData : data;
  const minInterval = min ?? (currentData?.mspInterval.min || 1);
  const maxInterval = max ?? (currentData?.mspInterval.max || 60);
  const hour = [60, 1440].includes(minInterval);
  const minutesPerUnit = hour ? 60 : 1;
  return (
    <FormProvider {...methods}>
      <form
        id={formId}
        onSubmit={(e) => {
          methods.handleSubmit(onSubmit, onError)(e);
        }}
      >
        {serverErrors?.msp_interval && <FormErrorAlerts errors={serverErrors?.msp_interval} />}
        <div className="row mb-4">
          <div className="col">
            {note ?? "Note: Existing intervals on selected Checks will be replaced with whatever you select below."}
          </div>
        </div>
        <SliderField
          name={ServiceField.INTERVAL}
          label={`Check Interval (${hour ? "hours" : "minutes"})`}
          defaultValue={currentData?.mspInterval.default || 5}
          min={minInterval}
          max={maxInterval}
          step={minutesPerUnit}
          inputScale={minutesPerUnit}
        />
      </form>
    </FormProvider>
  );
};
